import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./lib/i18n.jsx";
import { api, setToken } from "./lib/api";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import WorkerHome from "./pages/WorkerHome";
import MyProperty from "./pages/MyProperty";
import WorkerReports from "./pages/WorkerReports";
import Dashboard from "./pages/Dashboard";
import Warehouse from "./pages/Warehouse";
import { WarehousesTab, WarehouseCard, HistoryTab } from "./pages/WarehouseWarehouses";
import CatalogTab from "./pages/WarehouseCatalog";
import { AssetsTab, AssetCard } from "./pages/WarehouseAssets";
import { InventoryTab, InventoryCard } from "./pages/WarehouseInventory";
import { RequestsPage, RequestCard } from "./pages/Requests";
import RegistryTab from "./pages/WarehouseRegistry";
import Employees from "./pages/Employees";
import EmployeeCard from "./pages/EmployeeCard";
import SizesReport from "./pages/SizesReport";
import BadgeResolve from "./pages/BadgeResolve";
import Projects from "./pages/Projects";
import Reports from "./pages/Reports";
import Worktime from "./pages/Worktime";
import Analytics from "./pages/Analytics";
import Works from "./pages/Works";
import Norms from "./pages/Norms";
import Roles from "./pages/Roles";
import Organization from "./pages/Organization";
import ConfigPage from "./pages/ConfigPage";
import Audit from "./pages/Audit";
import Settings from "./pages/Settings";
import Constructor from "./pages/Constructor";
import Onboarding from "./pages/Onboarding";
import Profile from "./pages/Profile";
import WorkerDay from "./pages/WorkerDay";
import ThemeBuilder from "./pages/ThemeBuilder";
import Documents from "./pages/Documents";
import PlanFact from "./pages/PlanFact";
import PdfReports from "./pages/PdfReports";
import { isWorkerUser } from "./components/Layout";
import { useI18n } from "./lib/i18n.jsx";

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        setToken(data.token);
        await refresh();
      } catch (e) { /* ignore */ }
      window.history.replaceState(null, "", "/");
      navigate("/");
    })();
  }, [location, navigate, refresh]);
  return <div className="min-h-screen flex items-center justify-center text-slate-500">…</div>;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">{t("loading")}</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Shell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<Protected><Layout /></Protected>}>
        <Route index element={<Home />} />
        <Route path="worker" element={<WorkerHome />} />
        <Route path="my-property" element={<MyProperty />} />
        <Route path="worker/day" element={<WorkerDay />} />
        <Route path="worker/reports" element={<WorkerReports />} />
        <Route path="profile" element={<Profile />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="warehouse" element={<Warehouse />}>
          <Route index element={<WarehousesTab />} />
          <Route path="registry" element={<RegistryTab />} />
          <Route path="catalog" element={<CatalogTab />} />
          <Route path="assets" element={<AssetsTab />} />
          <Route path="history" element={<HistoryTab />} />
          <Route path="inventory" element={<InventoryTab />} />
        </Route>
        <Route path="warehouse/inventory/:id" element={<InventoryCard />} />
        <Route path="requests" element={<RequestsPage />} />
        <Route path="requests/:id" element={<RequestCard />} />
        <Route path="warehouse/warehouses/:id" element={<WarehouseCard />} />
        <Route path="warehouse/assets/:id" element={<AssetCard />} />
        <Route path="employees" element={<Employees />} />
        <Route path="employees/sizes" element={<SizesReport />} />
        <Route path="employees/:id" element={<EmployeeCard />} />
        <Route path="badge/:token" element={<BadgeResolve />} />
        <Route path="projects" element={<Projects />} />
        <Route path="reports" element={<Reports />} />
        <Route path="worktime" element={<Worktime />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="works" element={<Works />} />
        <Route path="norms" element={<Norms />} />
        <Route path="roles" element={<Roles />} />
        <Route path="organization" element={<Organization />} />
        <Route path="positions" element={<ConfigPage entity="positions" />} />
        <Route path="units" element={<ConfigPage entity="units" />} />
        <Route path="forms" element={<Constructor kind="forms" />} />
        <Route path="workflows" element={<Constructor kind="workflows" />} />
        <Route path="automations" element={<Constructor kind="automations" />} />
        <Route path="audit" element={<Audit />} />
        <Route path="settings" element={<Settings />} />
        <Route path="theme" element={<ThemeBuilder />} />
        <Route path="documents" element={<Documents />} />
        <Route path="plan-fact" element={<PlanFact />} />
        <Route path="pdf" element={<PdfReports />} />
        <Route path="onboarding" element={<Onboarding />} />
      </Route>
    </Routes>
  );
}

function Home() {
  const { user } = useAuth();
  return <Navigate to={isWorkerUser(user) ? "/worker" : "/dashboard"} replace />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
